import React from "react";
import { Refrigerator, Snowflake } from "lucide-react";
import { Field, Input, Select } from "./ui-kit";

const PROCESS_CHOICES = [
  { key: "rapid_cooling", label: "Frigorifero", description: "Il prodotto va conservato in frigo: raffreddamento rapido.", Icon: Refrigerator },
  { key: "blast_freezing", label: "Congelatore / Surgelatore", description: "Il prodotto va conservato congelato: congelamento per conservazione (almeno -18°C).", Icon: Snowflake },
];

// Campi dipendenti dal tipo di processo di abbattimento, condivisi fra la
// creazione (Abbattimento.jsx) e la modifica (ProdottiAbbattuti.jsx): tenerli
// in un solo posto evita che le due pagine divergano sulle regole di
// conformità (temperatura obiettivo, durata, conferma antiparassitaria).
export default function BlastChillingProcessFields({ form, setForm, setProcess, setProtocol }) {
  const chosen = !!form.process_type;
  return (
    <>
      <div className="sm:col-span-2 lg:col-span-3">
        <Field label="Tipo di processo *" hint="Come deve essere conservato il prodotto una volta abbattuto?">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {PROCESS_CHOICES.map(({ key, label, description, Icon }) => {
              const active = form.process_type === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setProcess(key)}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${active ? "border-gold bg-gold/10 ring-2 ring-gold/20" : `border-hairline bg-white hover:border-gold/60 ${!chosen ? "animate-pulse ring-2 ring-violet-300 ring-offset-1" : ""}`}`}
                  data-testid={`abbattimento-process-${key}`}
                >
                  <span className="flex items-center gap-2 font-heading font-semibold text-navy"><Icon className="h-5 w-5 text-gold" />{label}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-slate-500">{description}</span>
                </button>
              );
            })}
          </div>
        </Field>
        <button
          type="button"
          onClick={() => setProcess("parasite_freezing")}
          className={`mt-2 text-xs underline underline-offset-2 ${form.process_type === "parasite_freezing" ? "font-semibold text-navy" : "text-slate-500 hover:text-navy"}`}
          data-testid="abbattimento-process-parasite_freezing"
        >
          Serve invece il trattamento antiparassitario ittico (pesce crudo)?
        </button>
      </div>
      {form.process_type === "parasite_freezing" && (
        <Field label="Protocollo normativo *">
          <Select value={form.protocol} onChange={(e) => setProtocol(e.target.value)}>
            <option value="eu_-20_24h">−20 °C per almeno 24 ore</option>
            <option value="eu_-35_15h">−35 °C per almeno 15 ore</option>
          </Select>
        </Field>
      )}
      <Field label="Temperatura obiettivo *" hint={form.process_type === "blast_freezing" ? "Deve essere almeno -18°C" : undefined}>
        <Input type="number" step="0.1" value={form.target_temp} disabled={!chosen || form.process_type === "parasite_freezing"}
              onChange={(e) => setForm({ ...form, target_temp: e.target.value })} />
      </Field>
      {form.process_type !== "parasite_freezing" ? (
        <Field label="Durata massima prevista (minuti) *">
          <Input type="number" min="1" disabled={!chosen} value={form.max_duration_minutes} onChange={(e) => setForm({ ...form, max_duration_minutes: e.target.value })} />
        </Field>
      ) : (
        <Field label="Durata minima prevista (minuti)"><Input type="number" value={form.required_duration_minutes} disabled /></Field>
      )}
    </>
  );
}

export function BlastChillingConformityNote({ form, setForm }) {
  if (form.process_type === "parasite_freezing") {
    return (
      <label className={`mt-4 flex cursor-pointer items-start gap-3 rounded-xl border-2 p-4 ${form.entire_mass_confirmed ? "border-emerald-500 bg-emerald-50" : "border-red-300 bg-red-50/40"}`}>
        <input type="checkbox" checked={form.entire_mass_confirmed} onChange={(e) => setForm({ ...form, entire_mass_confirmed: e.target.checked })} className="mt-0.5 h-5 w-5 accent-emerald-600" />
        <span className="text-sm font-medium text-slate-700"><strong>Conferma obbligatoria:</strong> la temperatura prevista dal protocollo antiparassitario è stata raggiunta in ogni parte della massa del prodotto.</span>
      </label>
    );
  }
  if (!form.process_type) return null;
  return (
    <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4 text-sm text-blue-900">
      {form.process_type === "blast_freezing"
        ? "Il prodotto deve raggiungere una temperatura al cuore di almeno -18°C: è il congelamento per la conservazione a lungo termine, non il semplice raffreddamento in frigorifero. La conformità viene calcolata confrontando temperatura e durata con i limiti configurati nel Piano di Autocontrollo."
        : "La temperatura finale inserita deve corrispondere alla misurazione effettuata al cuore del prodotto. La conformità viene calcolata confrontando temperatura e durata con i limiti configurati nel Piano di Autocontrollo."}
    </div>
  );
}
